// Renders connectivity state as toasts:
// - offline: one sticky red toast with a live "retrying in Ns" countdown and a
//   "Retry now" button, plus an honest note about unsaved edits;
// - back online: closes it, flushes pending edits, and confirms in green.
import { Button } from '@heroui/react'
import { useEffect, useRef, useState } from 'react'

import { toast } from '@/lib/toast'
import { connectivity, useConnectivity } from '@/services/connectivity'
import { appStore, isApplicationLocked, useAppStore } from '@/store/app'

function useSecondsUntil(at: number | null): number | null {
	const compute = () => (at === null ? null : Math.max(0, Math.ceil((at - Date.now()) / 1000)))
	const [secs, setSecs] = useState<number | null>(compute)
	useEffect(() => {
		setSecs(compute())
		if (at === null) return
		const id = window.setInterval(() => setSecs(compute()), 250)
		return () => window.clearInterval(id)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [at])
	return secs
}

function hasUnsavedEdits(): boolean {
	const s = appStore.snapshot()
	return !!s.user && s.dirty && !isApplicationLocked(s.user.application_process)
}

export function OfflineDescription() {
	const { nextRetryAt, probing } = useConnectivity()
	const { dirty, user } = useAppStore()
	const secs = useSecondsUntil(nextRetryAt)
	const unsaved = dirty && !!user && !isApplicationLocked(user.application_process)

	const retry = probing ? 'Reconnecting…' : secs !== null ? `Retrying in ${secs}s.` : 'Retrying.'

	return (
		<span>
			{retry}{' '}
			{unsaved
				? 'Unsaved changes stay in this tab until we reconnect. Keep it open.'
				: 'You can keep working.'}
		</span>
	)
}

function RetryNowButton() {
	const { probing } = useConnectivity()
	return (
		<Button
			size="sm"
			variant="flat"
			color="danger"
			// The toast content row is a flex container; without this the button is
			// shrunk below its label width and the text overflows its padding.
			className="shrink-0"
			isLoading={probing}
			onPress={() => connectivity.retryNow()}
		>
			Retry now
		</Button>
	)
}

/**
 * When the API is back, push whatever the user changed while offline. Save
 * failures are reported by appStore.save itself.
 */
async function flushPendingChanges(): Promise<void> {
	const s = appStore.snapshot()
	const onAccount = window.location.pathname.startsWith('/account')
	const shouldFlush = hasUnsavedEdits() && !s.saving && onAccount
	if (!shouldFlush) {
		toast.success({ title: 'Connection restored' })
		return
	}
	try {
		await appStore.save({ source: 'reconnect' })
		toast.success({ title: 'Connection restored', description: 'Your changes have been saved.' })
	} catch {
		// appStore.save already told the user; edits stay dirty for the next retry.
	}
}

export default function ConnectivityToasts() {
	// Whether we are currently showing the offline toast, and its key (the key
	// can be null if the toast queue refused the toast; track them separately so
	// we never stack duplicates or miss the transition back online).
	const showingOffline = useRef(false)
	const offlineToastKey = useRef<string | null>(null)

	useEffect(() => {
		const handle = () => {
			const { status } = connectivity.snapshot()
			if (status === 'offline' && !showingOffline.current) {
				showingOffline.current = true
				offlineToastKey.current = toast.sticky('danger', {
					title: "Can't reach the server",
					description: <OfflineDescription />,
					endContent: <RetryNowButton />,
					hideCloseButton: true,
				})
			} else if (status === 'online' && showingOffline.current) {
				showingOffline.current = false
				if (offlineToastKey.current !== null) toast.close(offlineToastKey.current)
				offlineToastKey.current = null
				void flushPendingChanges()
			}
		}
		handle()
		return connectivity.subscribe(handle)
	}, [])

	return null
}
